import React, { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiCheckCircle, FiPlus } from 'shared/ui/fiIcons';
import { useI18n } from '../../context/I18nContext';
import { type CalendarSpace, getCalendarSpaceOnline } from './calendarStorage';
import { CalendarTaskModal } from './components/CalendarTaskModal';
import {
  type CalendarTask,
  type CalendarTaskOccurrence,
  type CalendarTaskLabel,
  completeCalendarTaskOccurrenceOnline,
  getCalendarLabelsOnline,
  getCalendarTaskOccurrencesOnline,
  getCalendarTaskSeriesId,
  getCalendarTasksOnline,
} from './calendarTaskStorage';

const dateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const todayKey = () => dateKey(new Date());

export const CalendarHabitsScreen: React.FC = () => {
  const { t } = useI18n();
  const { calendarId } = useParams();
  const [calendarSpace, setCalendarSpace] = React.useState<CalendarSpace | null>(null);
  const [isCalendarLoading, setIsCalendarLoading] = React.useState(true);
  const [tasks, setTasks] = React.useState<CalendarTask[]>([]);
  const [occurrences, setOccurrences] = React.useState<CalendarTaskOccurrence[]>([]);
  const [labels, setLabels] = React.useState<CalendarTaskLabel[]>([]);
  const [editingTask, setEditingTask] = React.useState<CalendarTask | null>(null);
  const [taskDraft, setTaskDraft] = React.useState<Partial<CalendarTask> | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = React.useState(false);
  const currentDateKey = todayKey();
  const monthDays = useMemo(() => {
    const now = new Date();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, index) => {
      const date = new Date(now.getFullYear(), now.getMonth(), index + 1);
      return {
        key: dateKey(date),
        date,
        day: index + 1,
      };
    });
  }, []);
  const occurrenceRange = useMemo(() => ({
    start: monthDays[0]?.key || currentDateKey,
    end: monthDays[monthDays.length - 1]?.key || currentDateKey,
  }), [currentDateKey, monthDays]);

  React.useEffect(() => {
    let isMounted = true;
    const loadCalendarSpace = async () => {
      setIsCalendarLoading(true);
      const nextCalendarSpace = await getCalendarSpaceOnline(calendarId);
      if (!isMounted) return;
      setCalendarSpace(nextCalendarSpace);
      setIsCalendarLoading(false);
    };
    void loadCalendarSpace();
    return () => {
      isMounted = false;
    };
  }, [calendarId]);

  const loadData = React.useCallback(async () => {
    if (!calendarSpace) return;
    const [nextTasks, nextLabels, nextOccurrences] = await Promise.all([
      getCalendarTasksOnline(calendarSpace.id),
      getCalendarLabelsOnline(calendarSpace.id),
      getCalendarTaskOccurrencesOnline(calendarSpace.id, occurrenceRange.start, occurrenceRange.end),
    ]);
    setTasks(nextTasks);
    setLabels(nextLabels);
    setOccurrences(nextOccurrences);
  }, [calendarSpace, occurrenceRange.end, occurrenceRange.start]);

  React.useEffect(() => {
    void loadData();
  }, [loadData]);

  if (isCalendarLoading) {
    return <div className="loading-state">{t('common.loading')}</div>;
  }

  if (!calendarSpace) {
    return <Navigate to="/boards" replace />;
  }

  const taskById = new Map(tasks.map(task => [task.id, task]));
  const getRootTaskId = (taskId: string) => {
    const visited = new Set<string>();
    let currentId = taskId;
    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const currentTask = taskById.get(currentId);
      if (!currentTask?.recurrenceParentId) return currentId;
      currentId = currentTask.recurrenceParentId;
    }
    return taskId;
  };
  const getOccurrenceGroupId = (entry: CalendarTaskOccurrence) => (
    entry.repeatType === 'none' ? entry.parentTaskId : getRootTaskId(entry.parentTaskId)
  );
  const todayOccurrenceMap = new Map<string, CalendarTaskOccurrence>();
  occurrences
    .filter(entry => dateKey(new Date(entry.startAt)) === currentDateKey)
    .sort((left, right) => new Date(left.startAt).getTime() - new Date(right.startAt).getTime())
    .forEach(entry => {
      const groupId = getOccurrenceGroupId(entry);
      if (!todayOccurrenceMap.has(groupId)) todayOccurrenceMap.set(groupId, entry);
    });
  const todayOccurrences = Array.from(todayOccurrenceMap.values());
  const habitRootIds = new Set(
    occurrences
      .filter(entry => entry.repeatType !== 'none')
      .map(entry => getRootTaskId(entry.parentTaskId))
  );
  const habits = Array.from(habitRootIds).map(rootId => {
    const groupOccurrences = occurrences
      .filter(entry => entry.repeatType !== 'none' && getRootTaskId(entry.parentTaskId) === rootId)
      .sort((left, right) => new Date(right.startAt).getTime() - new Date(left.startAt).getTime());
    return groupOccurrences[0] || tasks.find(task => task.id === rootId) || null;
  }).filter(Boolean) as Array<CalendarTask | CalendarTaskOccurrence>;
  const occurrencesForHabitOnDate = (habitRootId: string, date: string) => occurrences.filter(entry => (
    entry.repeatType !== 'none' &&
    getRootTaskId(entry.parentTaskId) === habitRootId &&
    dateKey(new Date(entry.startAt)) === date
  ));
  const isHabitDoneOnDate = (habitRootId: string, date: string) => (
    occurrencesForHabitOnDate(habitRootId, date).some(entry => entry.isCompleted)
  );
  const completedToday = todayOccurrences.filter(task => task.isCompleted).length;

  const toggleTodayTask = async (task: CalendarTaskOccurrence) => {
    await completeCalendarTaskOccurrenceOnline(
      calendarSpace.id,
      task.parentTaskId,
      task.occurrenceDate,
      !task.isCompleted
    );
    await loadData();
  };

  const openCreateTodayTask = () => {
    const start = new Date();
    start.setHours(9, 0, 0, 0);
    setTaskDraft({
      repeatType: 'none',
      startAt: start.toISOString(),
      durationMinutes: 30,
      customRepeatDays: [],
      labelIds: [],
      checklists: [],
      attachments: [],
    });
    setEditingTask(null);
    setIsTaskModalOpen(true);
  };

  return (
    <div className="calendar-board-page habits-page">
      <header className="calendar-board-header">
        <div className="calendar-board-header-main">
          <Link to={`/calendar/${calendarSpace.id}`} className="calendar-board-back" aria-label={t('calendar.backToBoards')}>
            <FiArrowLeft aria-hidden="true" />
          </Link>
          <div>
            <span className="calendar-board-kicker">{t('habits.kicker')}</span>
            <h1>{t('habits.title')}</h1>
          </div>
        </div>
        <button type="button" className="calendar-board-add" onClick={openCreateTodayTask}>
          <FiPlus aria-hidden="true" />
          <span>{t('habits.add')}</span>
        </button>
      </header>

      <main className="habits-layout">
        <section className="habits-summary">
          <div>
            <span>{t('habits.today')}</span>
            <strong>{completedToday}/{todayOccurrences.length}</strong>
            <p>{t('habits.todayDescription')}</p>
          </div>
          <FiCheckCircle aria-hidden="true" />
        </section>

        <section className="habits-chart habits-matrix-card">
          <div className="habits-chart-header">
            <h2>{t('habits.monthStats')}</h2>
            <span>{t('habits.monthStatsDescription')}</span>
          </div>
          {habits.length ? (
            <div className="habits-matrix">
              <div className="habits-matrix-head" style={{ ['--habit-days' as string]: monthDays.length }}>
                <span />
                {monthDays.map(day => (
                  <strong key={day.key}>{day.day}</strong>
                ))}
              </div>
              {habits.map(task => {
                const habitRootId = getRootTaskId(getCalendarTaskSeriesId(task));
                return (
                  <div key={habitRootId} className="habits-matrix-row" style={{ ['--habit-days' as string]: monthDays.length }}>
                    <div className="habits-matrix-title">
                      <i style={{ background: task.headerColor || '#2563eb' }} />
                      <span>{task.title}</span>
                    </div>
                    {monthDays.map(day => {
                      const isScheduled = occurrencesForHabitOnDate(habitRootId, day.key).length > 0;
                      const isDone = isHabitDoneOnDate(habitRootId, day.key);
                      return (
                        <span
                          key={`${habitRootId}-${day.key}`}
                          className={[
                            'habits-matrix-dot',
                            isScheduled ? 'scheduled' : '',
                            isDone ? 'done' : '',
                          ].filter(Boolean).join(' ')}
                          aria-label={`${task.title} ${day.day}`}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="habits-matrix-empty">{t('habits.emptyDescription')}</p>
          )}
        </section>

        <section className="habits-list">
          {todayOccurrences.length === 0 ? (
            <div className="habits-empty">
              <h2>{t('habits.emptyTitle')}</h2>
              <p>{t('habits.emptyDescription')}</p>
              <button type="button" className="calendar-board-add" onClick={openCreateTodayTask}>
                <FiPlus aria-hidden="true" />
                {t('habits.add')}
              </button>
            </div>
          ) : todayOccurrences.map(task => {
            return (
              <article key={task.occurrenceKey} className={`habit-card ${task.isCompleted ? 'completed' : ''}`}>
                <div className="habit-card-color" style={{ background: task.headerColor || '#2563eb' }} />
                <div>
                  <h2>{task.title}</h2>
                  <p>{task.description || t('habits.noDescription')}</p>
                  <span>{t(`calendar.repeat.${task.repeatType}`)} · {Math.floor(task.durationMinutes / 60)}h {task.durationMinutes % 60}m</span>
                </div>
                <div className="habit-card-actions">
                  <button type="button" className="calendar-task-footer-button secondary" onClick={() => {
                    setEditingTask(task);
                    setTaskDraft(null);
                    setIsTaskModalOpen(true);
                  }}>
                    {t('common.edit')}
                  </button>
                  <button type="button" className="calendar-task-footer-button primary" onClick={() => { void toggleTodayTask(task); }}>
                    {task.isCompleted ? t('habits.unmark') : t('habits.mark')}
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      </main>

      <CalendarTaskModal
        calendarId={calendarSpace.id}
        isOpen={isTaskModalOpen}
        task={editingTask}
        seriesTaskId={editingTask ? getCalendarTaskSeriesId(editingTask) : null}
        draft={taskDraft}
        occurrenceStartAt={editingTask?.startAt || null}
        occurrenceDate={(editingTask as CalendarTaskOccurrence | null)?.occurrenceDate || null}
        tasks={tasks}
        labels={labels}
        onClose={() => {
          setIsTaskModalOpen(false);
          setEditingTask(null);
          setTaskDraft(null);
        }}
        onSaved={async () => {
          await loadData();
          setIsTaskModalOpen(false);
          setEditingTask(null);
          setTaskDraft(null);
        }}
        onDeleted={async () => {
          await loadData();
          setIsTaskModalOpen(false);
          setEditingTask(null);
          setTaskDraft(null);
        }}
        onLabelsChange={setLabels}
      />
    </div>
  );
};
